import { Duration, Effect, Random, Schedule } from "effect"

/**
 * Reconnect timing with jitter (performance plan item 5). After a deploy every open tab loses its sockets at the same
 * moment; without jitter they would all come back at the same moments too. Each wait is spread between half and one
 * and a half times its nominal length.
 */

/** A nominal wait in millis spread over [0.5, 1.5) of itself, for a random draw in [0, 1). */
export const spread = (millis: number, random: number): number => Math.round(millis * (0.5 + random))

/** `duration` with jitter, as an effect (uses Effect's `Random`, so tests can pin it). */
export const jittered = (duration: Duration.Input): Effect.Effect<Duration.Duration> =>
  Effect.map(Random.next, (r) => Duration.millis(spread(Duration.toMillis(duration), r)))

/** Sleep for `duration` with jitter. */
export const sleepJittered = (duration: Duration.Input): Effect.Effect<void> =>
  Effect.flatMap(jittered(duration), (d) => Effect.sleep(d))

/** Exponential backoff from `base`, doubling, each wait capped at `capMillis` and then spread with jitter. */
export const jitteredBackoff = (
  base: Duration.Input,
  capMillis: number,
): Schedule.Schedule<unknown, unknown> =>
  Schedule.exponential(base, 2).pipe(
    Schedule.modifyDelay(({ duration }) =>
      Effect.map(Random.next, (r) =>
        Duration.millis(spread(Math.min(Duration.toMillis(duration), capMillis), r)),
      ),
    ),
  )
